import { useState, useEffect } from 'react';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';
import VerLinksButton from './VerLinksButton';

const PAGE_SIZE = 20;

const inputSt = {
  width: '100%', padding: '8px 10px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px', color: theme.colors.text,
  fontSize: '0.88rem', boxSizing: 'border-box', outline: 'none',
};
const labelSt = { display: 'block', color: theme.colors.textMuted, fontSize: '0.82rem', marginBottom: '3px' };
const fieldSt = { marginBottom: '0.8rem' };

const EMPTY_FORM = {
  id: '', title: '', description: '', availability: 'in stock',
  condition: 'new', price: '', link: '', brand: '', category: '', inventory: '',
};

function Products({ token }) {
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [showForm, setShowForm] = useState(false);
  const [editPk, setEditPk] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState('');

  const [confirmDeletePk, setConfirmDeletePk] = useState(null);
  const [deletingPk, setDeletingPk] = useState(null);

  const [notice, setNotice] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = () => {
    setLoading(true);
    fetch(`/api/products?page=${page}`, { headers })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [page, token]);

  const openCreate = () => {
    setEditPk(null);
    setForm(EMPTY_FORM);
    setPrimaryImage(null);
    setAdditionalImages([]);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditPk(row.pk);
    setForm({ id: row.id || '', title: row.title, description: row.description || '', availability: row.availability, condition: row.condition, price: row.price, link: row.link || '', brand: row.brand || '', category: row.category || '', inventory: row.inventory || '' });
    setPrimaryImage(null);
    setAdditionalImages([]);
    setFormError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price) return setFormError('Título y precio son requeridos');
    setSubmitting(true);
    setFormError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      if (primaryImage) fd.append('image', primaryImage);
      additionalImages.forEach(f => fd.append('additional_images', f));
      const url = editPk ? `/api/products/${editPk}` : '/api/products';
      const method = editPk ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: fd });
      const d = await res.json();
      if (!res.ok) return setFormError(d.message || 'Error al guardar');
      setNotice(d.meta_warning ? `⚠️ Guardado localmente, pero ${d.meta_warning}` : '');
      setShowForm(false);
      fetchData();
    } catch { setFormError('Error de conexión'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (pk) => {
    if (confirmDeletePk !== pk) { setConfirmDeletePk(pk); return; }
    setDeletingPk(pk);
    setConfirmDeletePk(null);
    try {
      const res = await fetch(`/api/products/${pk}`, { method: 'DELETE', headers });
      const d = await res.json().catch(() => ({}));
      setNotice(d.meta_warning ? `⚠️ Eliminado localmente, pero ${d.meta_warning}` : '');
      fetchData();
    } finally { setDeletingPk(null); }
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await fetch('/api/products/export', { headers });
      if (!r.ok) { setNotice('⚠️ No se pudieron exportar los productos'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'productos.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { setNotice('⚠️ No se pudieron exportar los productos'); }
    finally { setExporting(false); }
  };

  const downloadTemplate = () => {
    fetch('/api/products/template', { headers })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'plantilla_productos.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
  };

  const handleImport = async () => {
    if (!importFile) return setImportMsg('Selecciona un archivo');
    setImporting(true);
    setImportMsg('');
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await fetch('/api/products/import', { method: 'POST', headers, body: fd });
      const d = await res.json();
      if (res.ok) {
        setImportMsg(d.meta_warning ? `${d.message}. ⚠️ ${d.meta_warning}` : d.message);
        fetchData();
        setImportFile(null);
      } else {
        setImportMsg(d.message || 'Error al importar');
      }
    } catch { setImportMsg('Error de conexión'); }
    finally { setImporting(false); }
  };

  const formGrid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.8rem' };

  return (
    <main style={{ padding: isMobile ? '1.2rem 0.8rem' : '2rem 1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.2rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', margin: 0 }}>Productos</h1>
          <div style={{ display: 'flex', gap: '0.75rem', width: isMobile ? '100%' : 'auto' }}>
            <button onClick={handleExport} disabled={exporting} style={{ flex: isMobile ? 1 : 'none', padding: '8px 16px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.text, cursor: exporting ? 'not-allowed' : 'pointer', fontSize: '0.88rem', fontWeight: '600' }}>
              {exporting ? 'Exportando...' : 'Exportar'}
            </button>
            <button onClick={() => { setShowImport(true); setImportMsg(''); }} style={{ flex: isMobile ? 1 : 'none', padding: '8px 16px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.primary}`, borderRadius: '7px', color: theme.colors.primary, cursor: 'pointer', fontSize: '0.88rem', fontWeight: '600' }}>
              Importar
            </button>
            <button onClick={openCreate} style={{ flex: isMobile ? 1 : 'none', padding: '8px 20px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer' }}>
              + Crear
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <VerLinksButton />
        </div>

        {notice && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', backgroundColor: 'rgba(245, 158, 11, 0.12)', border: '1px solid rgba(245, 158, 11, 0.5)', borderRadius: '8px', padding: '10px 14px', marginBottom: '1.2rem', color: '#f59e0b', fontSize: '0.85rem' }}>
            <span>{notice}</span>
            <button onClick={() => setNotice('')} style={{ background: 'transparent', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '1rem', lineHeight: 1 }}>×</button>
          </div>
        )}

        {loading ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '2rem' }}>Cargando...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  {['ID', 'Título', 'Disponibilidad', 'Precio', 'Marca', 'Categoría', 'Imagen', ''].map(col => (
                    <th key={col} style={{ textAlign: 'left', padding: '10px 10px', borderBottom: `1px solid ${theme.colors.border}`, color: theme.colors.primary, fontWeight: '600', whiteSpace: 'nowrap' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>Sin productos. Crea el primero.</td></tr>
                ) : data.map(row => (
                  <tr key={row.pk} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, fontSize: '0.8rem' }}>{row.id || row.pk}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text, fontWeight: '500', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</td>
                    <td style={{ padding: '8px 10px', color: row.availability === 'in stock' ? theme.colors.success : theme.colors.error, fontSize: '0.8rem' }}>{row.availability === 'in stock' ? 'En stock' : 'Agotado'}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text }}>${parseFloat(row.price).toLocaleString('es-CO')}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted }}>{row.brand || '—'}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted }}>{row.category || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {row.image_link ? <img src={row.image_link} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} /> : '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <button onClick={() => openEdit(row)} style={{ padding: '4px 10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.primary}`, borderRadius: '6px', color: theme.colors.primary, cursor: 'pointer', fontSize: '0.78rem' }}>Editar</button>
                        <button onClick={() => handleDelete(row.pk)} disabled={deletingPk === row.pk} style={{ padding: '4px 10px', backgroundColor: confirmDeletePk === row.pk ? theme.colors.error : 'transparent', border: `1px solid ${theme.colors.error}`, borderRadius: '6px', color: confirmDeletePk === row.pk ? '#fff' : theme.colors.error, cursor: 'pointer', fontSize: '0.78rem' }}>
                          {confirmDeletePk === row.pk ? '¿Confirmar?' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.2rem' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === 1 ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>← Anterior</button>
          <span style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>Página {page} de {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === totalPages ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>Siguiente →</button>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem', overflowY: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.2rem' : '2rem', width: '100%', maxWidth: '640px', boxShadow: '0 8px 40px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem', marginBottom: '1.5rem' }}>{editPk ? 'Editar producto' : 'Nuevo producto'}</h2>
            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <div style={fieldSt}>
                  <label style={labelSt}>ID del producto (opcional)</label>
                  <input value={form.id} onChange={e => setForm(f => ({ ...f, id: e.target.value }))} style={inputSt} placeholder="ej: PROD-001" />
                </div>
                <div style={fieldSt}>
                  <label style={labelSt}>Título *</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputSt} required />
                </div>
              </div>
              <div style={fieldSt}>
                <label style={labelSt}>Descripción</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} rows={3} />
              </div>
              <div style={formGrid}>
                <div style={fieldSt}>
                  <label style={labelSt}>Disponibilidad *</label>
                  <select value={form.availability} onChange={e => setForm(f => ({ ...f, availability: e.target.value }))} style={{ ...inputSt, cursor: 'pointer' }}>
                    <option value="in stock">En stock</option>
                    <option value="out of stock">Agotado</option>
                  </select>
                </div>
                <div style={fieldSt}>
                  <label style={labelSt}>Condición *</label>
                  <select value={form.condition} onChange={e => setForm(f => ({ ...f, condition: e.target.value }))} style={{ ...inputSt, cursor: 'pointer' }}>
                    <option value="new">Nuevo</option>
                    <option value="refurbished">Reacondicionado</option>
                    <option value="used">Usado</option>
                  </select>
                </div>
                <div style={fieldSt}>
                  <label style={labelSt}>Precio * (moneda local)</label>
                  <input type="text" inputMode="numeric" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value.replace(/\D/g, '') }))} style={inputSt} placeholder="ej: 50000" required />
                </div>
                <div style={fieldSt}>
                  <label style={labelSt}>Marca</label>
                  <input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} style={inputSt} />
                </div>
                <div style={fieldSt}>
                  <label style={labelSt}>Categoría (opcional)</label>
                  <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inputSt} placeholder="ej: Camisas" />
                </div>
                <div style={fieldSt}>
                  <label style={labelSt}>Inventario</label>
                  <input type="number" value={form.inventory} onChange={e => setForm(f => ({ ...f, inventory: e.target.value }))} onWheel={e => e.currentTarget.blur()} style={inputSt} />
                </div>
                <div style={fieldSt}>
                  <label style={labelSt}>Link (opcional)</label>
                  <input value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} style={inputSt} placeholder="https://..." />
                </div>
              </div>
              <div style={fieldSt}>
                <label style={labelSt}>Imagen principal (se redimensiona a 500×500 automáticamente)</label>
                <input type="file" accept="image/*" onChange={e => setPrimaryImage(e.target.files[0])} style={{ color: theme.colors.text, fontSize: '0.85rem' }} />
                {primaryImage && <p style={{ color: theme.colors.success, fontSize: '0.78rem', marginTop: '3px' }}>✓ {primaryImage.name}</p>}
              </div>
              <div style={fieldSt}>
                <label style={labelSt}>Imágenes o videos adicionales (hasta 9)</label>
                <input type="file" accept="image/*,video/*" multiple onChange={e => setAdditionalImages(Array.from(e.target.files))} style={{ color: theme.colors.text, fontSize: '0.85rem' }} />
                {additionalImages.length > 0 && <p style={{ color: theme.colors.success, fontSize: '0.78rem', marginTop: '3px' }}>✓ {additionalImages.length} archivo(s) seleccionado(s)</p>}
                <p style={{ color: theme.colors.textMuted, fontSize: '0.76rem', marginTop: '3px' }}>Puedes subir imágenes y videos. Los videos se reproducen en la tienda; solo las imágenes se sincronizan con el catálogo de Meta.</p>
              </div>
              {formError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{formError}</p>}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button type="button" onClick={() => setShowForm(false)} disabled={submitting} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem' }}>Cancelar</button>
                <button type="submit" disabled={submitting} style={{ flex: 1, padding: '10px', backgroundColor: submitting ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>
                  {submitting ? 'Guardando...' : (editPk ? 'Actualizar' : 'Crear producto')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div onClick={() => { if (!importing) setShowImport(false); }} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.2rem' : '2rem', width: '100%', maxWidth: '440px', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem', marginBottom: '1.5rem' }}>Importar productos</h2>
            <button onClick={downloadTemplate} style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.primary}`, borderRadius: '7px', color: theme.colors.primary, cursor: 'pointer', fontSize: '0.9rem', marginBottom: '1.2rem' }}>
              📥 Descargar plantilla Excel
            </button>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', marginBottom: '1rem' }}>
              En la importación masiva usa URLs para las imágenes (no subidas de archivo).
            </p>
            <div style={fieldSt}>
              <label style={labelSt}>Seleccionar archivo .xlsx</label>
              <input type="file" accept=".xlsx,.xls" onChange={e => setImportFile(e.target.files[0])} style={{ color: theme.colors.text, fontSize: '0.85rem' }} />
            </div>
            {importMsg && <p style={{ color: importMsg.includes('⚠️') ? '#f59e0b' : (importMsg.includes('importado') ? theme.colors.success : theme.colors.error), fontSize: '0.85rem', marginBottom: '1rem' }}>{importMsg}</p>}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowImport(false)} disabled={importing} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem' }}>Cerrar</button>
              <button onClick={handleImport} disabled={importing || !importFile} style={{ flex: 1, padding: '10px', backgroundColor: importing || !importFile ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: importing || !importFile ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>
                {importing ? 'Importando...' : 'Importar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Products;
